'use client';
import React, { useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';

const collectionData = [
  { month: 'Jan', actual: 58.2, target: 65.3, customs: 28.1 },
  { month: 'Feb', actual: 72.1, target: 65.3, customs: 31.4 },
  { month: 'Mar', actual: 69.8, target: 68.0, customs: 29.7 },
  { month: 'Apr', actual: 81.4, target: 72.5, customs: 34.2 },
  { month: 'May', actual: 94.7, target: 80.1, customs: 38.4 },
  { month: 'Jun', actual: null, target: 85.2, customs: null },
  { month: 'Jul', actual: null, target: 92.0, customs: null },
  { month: 'Aug', actual: null, target: 98.4, customs: null },
  { month: 'Sep', actual: null, target: 105.0, customs: null },
  { month: 'Oct', actual: null, target: 112.8, customs: null },
  { month: 'Nov', actual: null, target: 118.6, customs: null },
  { month: 'Dec', actual: null, target: 123.0, customs: null },
];

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      className="rounded-lg px-3 py-2 shadow-xl text-xs"
      style={{
        backgroundColor: 'var(--dark-elevated)',
        border: '1px solid var(--dark-border)',
        color: 'var(--dark-text)',
      }}
    >
      <p className="font-semibold mb-1">{label} 2026</p>
      {payload.map((entry: any) => (
        <div key={`tip-${entry.name}`} className="flex items-center gap-2 mb-0.5">
          <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: entry.color }} />
          <span style={{ color: 'var(--dark-muted)' }}>{entry.name}:</span>
          <span className="font-semibold tabular-nums">
            {entry.value !== null ? `$${entry.value}M` : '—'}
          </span>
        </div>
      ))}
    </div>
  );
};

export default function CollectionsTrendChart() {
  const [activeLines, setActiveLines] = useState({
    actual: true,
    target: true,
    customs: true,
  });

  return (
    <div
      className="rounded-xl p-5 h-full"
      style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold" style={{ color: 'var(--dark-text)' }}>
            Monthly Collections vs Target
          </h3>
          <p className="text-xs mt-0.5" style={{ color: 'var(--dark-muted)' }}>
            FY 2026 · USD Millions · Shaded area = forecast
          </p>
        </div>
        <div className="flex gap-2">
          {(['actual', 'target', 'customs'] as const).map((key) => (
            <button
              key={`toggle-${key}`}
              onClick={() => setActiveLines((p) => ({ ...p, [key]: !p[key] }))}
              className="text-xs px-2 py-1 rounded-md font-medium transition-all duration-150"
              style={{
                backgroundColor: activeLines[key]
                  ? key === 'actual' ? 'rgba(212,160,23,0.2)'
                    : key === 'target' ? 'rgba(96,165,250,0.2)' : 'rgba(52,211,153,0.2)'
                  : 'rgba(255,255,255,0.05)',
                color: activeLines[key]
                  ? key === 'actual' ? 'var(--accent)'
                    : key === 'target' ? '#60a5fa' : '#34d399' : 'var(--dark-muted)',
                border: '1px solid transparent',
              }}
            >
              {key === 'actual' ? 'Collected' : key === 'target' ? 'Target' : 'Customs'}
            </button>
          ))}
        </div>
      </div>
      <ResponsiveContainer width="100%" height={260}>
        <AreaChart data={collectionData} margin={{ top: 4, right: 4, left: -10, bottom: 0 }}>
          <defs>
            <linearGradient id="gradActual" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="var(--accent)" stopOpacity={0.3} />
              <stop offset="95%" stopColor="var(--accent)" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="gradTarget" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#60a5fa" stopOpacity={0.15} />
              <stop offset="95%" stopColor="#60a5fa" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="gradCustoms" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#34d399" stopOpacity={0.2} />
              <stop offset="95%" stopColor="#34d399" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="var(--dark-border)" strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="month"
            tick={{ fontSize: 11, fill: 'var(--dark-muted)' }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 11, fill: 'var(--dark-muted)' }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v) => `$${v}M`}
          />
          <Tooltip content={<CustomTooltip />} />
          <ReferenceLine
            x="May"
            stroke="rgba(255,255,255,0.15)"
            strokeDasharray="4 2"
            label={{ value: 'Today', fill: 'var(--dark-muted)', fontSize: 10 }}
          />
          {activeLines.target && (
            <Area
              type="monotone"
              dataKey="target"
              name="Target"
              stroke="#60a5fa"
              strokeWidth={1.5}
              strokeDasharray="5 3"
              fill="url(#gradTarget)"
              dot={false}
              connectNulls
            />
          )}
          {activeLines.actual && (
            <Area
              type="monotone"
              dataKey="actual"
              name="Collected"
              stroke="var(--accent)"
              strokeWidth={2.5}
              fill="url(#gradActual)"
              dot={{ r: 3, fill: 'var(--accent)', strokeWidth: 0 }}
              connectNulls={false}
            />
          )}
          {activeLines.customs && (
            <Area
              type="monotone"
              dataKey="customs"
              name="Customs"
              stroke="#34d399"
              strokeWidth={1.5}
              fill="url(#gradCustoms)"
              dot={false}
              connectNulls={false}
            />
          )}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}